// Formulas written again when cells move, as Google Sheets does it: a formula pasted or filled elsewhere moves its
// relative references by as much ($A$1 stays), rows or columns put in push the references after them along (a block
// grows when they go inside it), rows or columns taken out pull them back and turn a reference to a deleted cell into
// #REF!, and a tab renamed is renamed in every formula that names it. Only the references change: every other
// character stays as the user typed it.

import { MAX_COLS, MAX_ROWS } from "./addr";
import { ParseError, refText, tokenize, type Ref, type RefEnd } from "./parse";
import { isFormula } from "./engine";

/** A formula with each reference given to f, written back; anything else as it was. */
export function mapRefs(raw: string, f: (ref: Ref) => Ref): string {
  if (!isFormula(raw)) return raw;
  const src = raw.slice(1);
  let toks;
  try {
    toks = tokenize(src);
  } catch (e) {
    if (e instanceof ParseError) return raw;
    throw e;
  }
  let out = "";
  let at = 0;
  for (const t of toks) {
    if (t.k !== "ref") continue;
    const next = f(t.ref);
    if (next === t.ref) continue;
    out += src.slice(at, t.pos) + refText(next);
    at = t.end;
  }
  return at === 0 ? raw : "=" + out + src.slice(at);
}

const dead = (ref: Ref): Ref => ({ ...ref, dead: true });

/** A formula moved by dr rows and dc columns (a paste, a fill): its relative references move with it. */
export function moveFormula(raw: string, dr: number, dc: number): string {
  if (!dr && !dc) return raw;
  return mapRefs(raw, (ref) => {
    if (ref.dead) return ref;
    const move = (e: RefEnd): RefEnd | null => {
      const r = e.r === null || e.ar ? e.r : e.r + dr;
      const c = e.c === null || e.ac ? e.c : e.c + dc;
      if ((r !== null && (r < 0 || r >= MAX_ROWS)) || (c !== null && (c < 0 || c >= MAX_COLS))) return null;
      return { ...e, r, c };
    };
    const a = move(ref.a);
    const b = ref.b ? move(ref.b) : null;
    if (!a || (ref.b && !b)) return dead(ref);
    return { ...ref, a, b };
  });
}

export interface Change {
  /** the tab whose rows or columns change, by name */
  tab: string;
  axis: "row" | "col";
  /** where, from 0, and how many */
  at: number;
  n: number;
  /** put in, or taken out */
  del: boolean;
}

/** Whether a reference in a formula on tab `on` points at the tab a change is made to. */
function onTab(ref: Ref, on: string, tab: string): boolean {
  return (ref.sheet ?? on).toLowerCase() === tab.toLowerCase();
}

/** A formula on tab `on` written again for rows or columns put in or taken out on a tab. */
export function shiftFormula(raw: string, on: string, ch: Change): string {
  return mapRefs(raw, (ref) => {
    if (ref.dead || !onTab(ref, on, ch.tab)) return ref;
    const key = ch.axis === "row" ? "r" : "c";
    const abs = ch.axis === "row" ? "ar" : "ac";
    const get = (e: RefEnd) => e[key];
    const put = (e: RefEnd, v: number): RefEnd => ({ ...e, [key]: v, [abs]: e[abs] });
    const end = ch.at + ch.n;
    if (!ch.del) {
      const grow = (e: RefEnd) => (get(e) !== null && get(e)! >= ch.at ? put(e, get(e)! + ch.n) : e);
      const a = grow(ref.a);
      const b = ref.b ? grow(ref.b) : null;
      const max = ch.axis === "row" ? MAX_ROWS : MAX_COLS;
      if ((get(a) ?? 0) >= max || (b && (get(b) ?? 0) >= max)) return dead(ref);
      return a === ref.a && b === ref.b ? ref : { ...ref, a, b };
    }
    // taken out: a single cell inside is gone; a block keeps what is left of it
    if (!ref.b || get(ref.a) === null || get(ref.b) === null) {
      const one = (e: RefEnd): RefEnd | null => {
        const v = get(e);
        if (v === null) return e;
        if (v >= ch.at && v < end) return null;
        return v >= end ? put(e, v - ch.n) : e;
      };
      if (!ref.b) {
        const a = one(ref.a);
        return a ? (a === ref.a ? ref : { ...ref, a }) : dead(ref);
      }
      // a whole column or row on the other axis is left alone; a block that runs to the foot (A2:B) keeps running
      if (get(ref.a) === null && get(ref.b) === null) return ref;
      const open = get(ref.a) === null ? "a" : "b";
      const fixedEnd = open === "a" ? ref.b : ref.a;
      const v = get(fixedEnd)!;
      const moved = v >= end ? put(fixedEnd, v - ch.n) : v >= ch.at ? put(fixedEnd, ch.at) : fixedEnd;
      if (moved === fixedEnd) return ref;
      return open === "a" ? { ...ref, b: moved } : { ...ref, a: moved };
    }
    const lo = Math.min(get(ref.a)!, get(ref.b!)!);
    const hi = Math.max(get(ref.a)!, get(ref.b!)!);
    if (lo >= ch.at && hi < end) return dead(ref);
    const nlo = lo >= end ? lo - ch.n : lo >= ch.at ? ch.at : lo;
    const nhi = hi >= end ? hi - ch.n : hi >= ch.at ? ch.at - 1 : hi;
    const aIsLo = get(ref.a)! <= get(ref.b!)!;
    const a = put(ref.a, aIsLo ? nlo : nhi);
    const b = put(ref.b!, aIsLo ? nhi : nlo);
    return { ...ref, a, b };
  });
}

/** A formula with a tab's name changed wherever it names the tab. */
export function renameInFormula(raw: string, from: string, to: string): string {
  return mapRefs(raw, (ref) => (ref.sheet !== null && ref.sheet.toLowerCase() === from.toLowerCase() ? { ...ref, sheet: to } : ref));
}

/** Where a reference typed in the formula box sits, for pointing at cells: the token's start and end in the text. */
export function refsIn(raw: string): { pos: number; end: number; ref: Ref }[] {
  if (!isFormula(raw)) return [];
  try {
    return tokenize(raw.slice(1)).flatMap((t) => (t.k === "ref" ? [{ pos: t.pos + 1, end: t.end + 1, ref: t.ref }] : []));
  } catch {
    return [];
  }
}
